import React, { useEffect, useState } from 'react'

export default function PdfCalibrationModal({
  open,
  onClose,
  template,
  onSavePositions
}) {
  const [fieldPositions, setFieldPositions] = useState({})
  const [currentField, setCurrentField] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')

  useEffect(() => {
    if (template?.custom_template_path) {
      setPreviewUrl(`/api/files/${encodeURIComponent(template.custom_template_path)}`)
    }
    if (template?.pdf_field_positions) {
      setFieldPositions(template.pdf_field_positions)
    }
  }, [template])

  const fields = [
    { key: 'buyer_name', label: 'Nom du client' },
    { key: 'invoice_number', label: 'Numéro de facture' },
    { key: 'invoice_date', label: 'Date de facture' },
    { key: 'lines_start', label: 'Début des lignes (Y seulement)' },
    { key: 'col_product_name', label: 'Colonne Produit (X seulement)' },
    { key: 'col_qty', label: 'Colonne Quantité (X seulement)' },
    { key: 'col_unit_price', label: 'Colonne Prix unitaire (X seulement)' },
    { key: 'col_line_total', label: 'Colonne Total ligne (X seulement)' },
    { key: 'subtotal', label: 'Total HT' },
    { key: 'tva', label: 'TVA' },
    { key: 'total', label: 'Total TTC' }
  ]

  const handlePdfClick = (event) => {
    if (!currentField) return

    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const newPositions = { ...fieldPositions }

    if (currentField.key.includes('col_') || currentField.key === 'lines_start') {
      if (currentField.key === 'lines_start') {
        newPositions[currentField.key] = { y }
      } else {
        newPositions[currentField.key] = { x }
      }
    } else {
      newPositions[currentField.key] = { x, y, fontSize: 11 }
    }

    setFieldPositions(newPositions)
    setCurrentField(null)
  }

  const handleSave = () => {
    onSavePositions(fieldPositions)
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface-card rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-bold mb-4">📐 Calibration du PDF Template</h2>
        
        <div className="grid grid-cols-2 gap-6 h-[600px]">
          {/* PDF Preview */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-2">PDF Template - Cliquez pour positionner</h3>
            <div 
              className="border-2 border-dashed border-slate-400 h-[500px] overflow-auto cursor-crosshair relative"
              onClick={handlePdfClick}
            >
              {previewUrl && (
                <embed 
                  src={previewUrl} 
                  type="application/pdf" 
                  width="100%" 
                  height="500px" 
                />
              )}
              {/* Overlay pour capturer les clics */}
              <div
                className="absolute inset-0"
                style={{ pointerEvents: currentField ? 'auto' : 'none' }}
              />
            </div>
            {currentField && (
              <p className="text-slate-700 mt-2 text-sm">
                🎯 Mode calibration: Cliquez sur le PDF pour positionner "{currentField.label}"
              </p>
            )}
          </div>

          {/* Field Configuration */}
          <div className="overflow-auto">
            <h3 className="font-semibold mb-4">Champs à calibrer</h3>
            
            <div className="mb-4 p-3 bg-surface-primary rounded">
              <h4 className="font-medium mb-2">Instructions:</h4>
              <ul className="text-sm text-content-secondary space-y-1">
                <li>• Cliquez sur "Positionner" pour un champ</li>
                <li>• Puis cliquez sur le PDF à l'endroit souhaité</li>
                <li>• Les coordonnées X,Y sont sauvegardées automatiquement</li>
              </ul>
            </div>

            {fields.map(field => (
              <div
                key={field.key}
                className={`mb-3 p-3 border rounded ${
                  fieldPositions[field.key]
                    ? 'bg-green-50 border-green-200'
                    : 'bg-surface-card border-border'
                }`}
              >
                <strong className="text-sm">{field.label}</strong>
                <div className="flex gap-2 items-center mt-2">
                  <button 
                    onClick={() => setCurrentField(field)}
                    disabled={currentField?.key === field.key}
                    className={`px-3 py-1 text-xs rounded ${
                      currentField?.key === field.key 
                        ? 'bg-slate-900 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    {currentField?.key === field.key ? '🎯 En cours...' : '📍 Positionner'}
                  </button>
                  
                  {fieldPositions[field.key] && (
                    <span className="text-xs text-content-secondary">
                      Position: {JSON.stringify(fieldPositions[field.key])}
                    </span>
                  )}

                  {fieldPositions[field.key] &&
                    !field.key.includes('col_') &&
                    field.key !== 'lines_start' && (
                      <input 
                        type="number"
                        placeholder="Taille police"
                        value={fieldPositions[field.key]?.fontSize || 11}
                        onChange={(e) =>
                          setFieldPositions(prev => ({
                            ...prev,
                            [field.key]: {
                              ...prev[field.key],
                              fontSize: parseInt(e.target.value) || 11
                            }
                          }))
                        }
                        className="w-16 px-1 py-1 text-xs border rounded"
                      />
                    )}
                </div>
              </div>
            ))}

            {/* Line Height Configuration */}
            <div className="mt-4 p-3 border rounded bg-surface-card">
              <strong className="text-sm">📏 Hauteur entre les lignes</strong>
              <div className="mt-2">
                <input 
                  type="number"
                  placeholder="Hauteur (ex: 15)"
                  value={fieldPositions.line_height || 15}
                  onChange={(e) =>
                    setFieldPositions(prev => ({
                      ...prev,
                      line_height: parseInt(e.target.value) || 15
                    }))
                  }
                  className="w-20 px-2 py-1 text-sm border rounded"
                />
                <span className="ml-2 text-xs text-content-secondary">
                  pixels entre chaque ligne de produit
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded"
          >
            Annuler
          </button>
          <button 
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded"
          >
            💾 Sauvegarder les positions
          </button>
        </div>
      </div>
    </div>
  )
}
